import { Voice, VoiceValues } from '../../../common/src/model.ts';
import { AudioPlayer } from './audio_player.ts';
import { DualAudioElementInterface } from './dual_audio_element.tsx';

export class MP3AudioPlayer implements AudioPlayer {
  protected readonly abortController = new AbortController();
  protected readonly audioElement: DualAudioElementInterface;
  protected readonly endOfFile: () => void;
  protected readonly onDurationChange?: (duration: number) => void;
  protected readonly updateLocationSlider: (() => void) | undefined;
  private mp3RefreshCount = 0;
  initialTime = '0:00';
  getVoiceValues: () => VoiceValues;

  constructor({
    dualAudioElement,
    endOfFile,
    getVoiceValues,
    onDurationChange,
    updateLocationSlider,
  }: {
    dualAudioElement: DualAudioElementInterface;
    endOfFile: () => void;
    getVoiceValues: () => VoiceValues;
    onDurationChange?: (duration: number | undefined) => void;
    updateLocationSlider?: () => void;
  }) {
    this.audioElement = dualAudioElement;
    this.endOfFile = endOfFile;
    this.getVoiceValues = getVoiceValues;
    this.onDurationChange = onDurationChange;
    this.updateLocationSlider = updateLocationSlider;

    const { signal } = this.abortController;
    this.audioElement.addEventListener('ended', this.onEndOfFile.bind(this), { signal });
    this.audioElement.addEventListener('canplaythrough', this.onCanPlayThrough.bind(this), { signal });
  }

  play() {
    void this.audioElement.play();
  }

  pause() {
    this.audioElement.pause();
  }

  get duration(): number | undefined {
    // in seconds
    if (this.audioElement.duration === undefined || Number.isNaN(this.audioElement.duration)) {
      return;
    }
    return this.audioElement.duration;
  }

  setLocationPercentage(percent: number) {
    const { duration } = this.audioElement;
    if (duration === undefined) {
      console.error('Setting location percentage when duration is undefined');
      return;
    }
    this.audioElement.currentTime = duration * (percent / 100);
  }

  setLoop(loop: boolean) {
    this.audioElement.loop = loop;
  }

  onVolumeChange() {
    void this.refreshMP3();
  }

  onTempoChange() {
    this.onVolumeChange();
  }

  restart() {
    this.audioElement.currentTime = 0;
    this.updateLocationSlider?.();
  }

  getCurrentPercent(): number | undefined {
    const duration = this.duration;
    if (duration === undefined || duration === 0) {
      return undefined;
    }
    return (this.audioElement.currentTime / duration) * 100;
  }

  setNewTime(value: number) {
    const { duration } = this.audioElement;
    if (duration === undefined) {
      console.error('Setting new time when duration is undefined');
      return;
    }
    const sliderValue = value / 100;
    const newTime = sliderValue * duration;
    this.audioElement.currentTime = newTime;
  }

  get fileTempo(): number | undefined {
    throw new Error('fileTempo not implemented');
  }

  playFirstNote(_voice: Voice, _startTick?: number) {
    throw new Error('playFirstNote not implemented');
  }

  get ticksPerBeat(): number | undefined {
    return undefined;
  }

  protected onEndOfFile() {
    this.endOfFile();
  }

  protected onCanPlayThrough() {
    if (this.duration === undefined) {
      return;
    }
    this.onDurationChange?.(this.duration);
  }

  protected async refreshMP3() {
    throw new Error('refreshMP3 not implemented');
  }

  abortPreviousMP3Refresh(): number {
    return ++this.mp3RefreshCount;
  }

  shouldAbortMP3Refresh(currentIteration: number): boolean {
    return currentIteration < this.mp3RefreshCount;
  }

  destroy() {
    this.pause();
    this.abortController.abort();
  }

  // destroy() has been called: the next song's player has taken over
  protected get destroyed(): boolean {
    return this.abortController.signal.aborted;
  }
}
